import { types, Instance } from 'mobx-state-tree';

export interface IPlayer extends Instance<typeof Player> {}

// Gradient color type - each gradient has two colors that alternate
export type GradientColorPair = [string, string];

// Base gradient colors - vibrant, saturated gradients with high contrast
export const baseGradients: GradientColorPair[] = [
    ['#650CA5', '#56088D'], // Purple gradient
    ['#1D205A', '#1F2250'], // Blue gradient
];

// Additional gradient colors - softer gradients with noticeable contrast
export const additionalGradients: GradientColorPair[] = [
    // ['#FFB6C1', '#C71585'], // Pink gradient
    // ['#FFDAB9', '#CD853F'], // Peach gradient
    // ['#98FB98', '#2E8B57'], // Mint gradient
    // ['#E6E6FA', '#663399'], // Lavender gradient
    // ['#B0E0E6', '#4169E1'], // Powder blue gradient
    // ['#AFEEEE', '#008B8B'], // Aqua gradient
    // ['#B0C4DE', '#483D8B'], // Steel blue gradient
    // ['#FFFACD', '#DAA520'], // Lemon gradient
];

// All gradients combined
export const allGradients: GradientColorPair[] = [...baseGradients, ...additionalGradients];

// Legacy color arrays for backward compatibility (uses first color of each gradient)
export const baseColors = baseGradients.map(g => g[0]);
export const additionalColors = additionalGradients.map(g => g[0]);

// Get gradient colors by index (checkerboard pattern for 2x2 grid layout)
// Grid layout: [0,1] / [2,3] - we want alternating colors like a checkerboard
export function getGradientByIndex(index: number): [string, string] {
    // For a 2-column grid, calculate row and column
    const row = Math.floor(index / 2);
    const col = index % 2;
    // Checkerboard: (row + col) % 2 gives alternating pattern
    const gradientIndex = (row + col) % baseGradients.length;
    const gradient = baseGradients[gradientIndex];
    return [gradient[0], gradient[1]];
}

// Get gradient colors by index with inverted checkerboard pattern
export function getGradientByIndexInverted(index: number): [string, string] {
    // For a 2-column grid, calculate row and column
    const row = Math.floor(index / 2);
    const col = index % 2;
    // Inverted checkerboard: (row + col + 1) % 2 gives opposite pattern
    const gradientIndex = (row + col + 1) % baseGradients.length;
    const gradient = baseGradients[gradientIndex];
    return [gradient[0], gradient[1]];
}

// Get gradient colors from a base color (returns 2 colors)
export function getGradientColors(baseColor: string): [string, string] {
    // Find the gradient that starts with this color
    const gradient = allGradients.find(g => g[0] === baseColor);
    if (gradient) {
        return [gradient[0], gradient[1]];
    }
    // Fallback: return the color twice (solid color)
    return [baseColor, baseColor];
}

// Get color for a player by index (alternates through gradients)
export function getColorByIndex(index: number): string {
    return baseGradients[index % baseGradients.length][0];
}

let availableColors = [...baseColors];

function getRandomColor() {
    if (availableColors.length === 0) {
        availableColors = [...baseColors];
    }
    const randomIndex = Math.floor(Math.random() * availableColors.length);
    const color = availableColors[randomIndex];
    availableColors.splice(randomIndex, 1);
    return color;
}

const CommanderDamage = types
    .model('CommanderDamage', {
        opponentId: types.identifier,
        damage: types.number,
        backgroundColor: types.string,
        isMe: types.boolean,
    })
    .actions(self => ({
        increase(amount: number = 1) {
            self.damage = Math.min(21, self.damage + amount);
        },
        decrease(amount: number = 1) {
            self.damage = Math.max(0, self.damage - amount);
        },
    }));

const Player = types
    .model('Player', {
        id: types.identifier,
        name: types.optional(types.string, ''),
        health: types.number,
        backgroundColor: types.string,
        commanderDamage: types.optional(types.array(CommanderDamage), []),
        commanderDeaths: types.optional(types.number, 0),
    })
    .actions(self => ({
        increase(amount = 1) {
            self.health += amount;
        },
        decrease(amount = 1) {
            self.health = self.health - amount;
        },
        setName(name: string) {
            self.name = name;
        },
        setBackgroundColor(color: string) {
            self.backgroundColor = color;
        },
        increaseCommanderDamage(opponentId: string, amount = 1) {
            const entry = self.commanderDamage.find(e => e.opponentId === opponentId);
            if (entry) {
                const prevDamage = entry.damage;
                entry.increase(amount);
                const actualIncrease = entry.damage - prevDamage;
                self.health = Math.max(0, self.health - actualIncrease);
            }
        },
        decreaseCommanderDamage(opponentId: string, amount = 1) {
            const entry = self.commanderDamage.find(e => e.opponentId === opponentId);
            if (entry && entry.damage >= amount) {
                self.health += amount;
                entry.decrease(amount);
            } else if (entry && entry.damage > 0) {
                const actualAmount = entry.damage;
                self.health += actualAmount;
                entry.decrease(actualAmount);
            }
        },
        increaseCommanderDeaths() {
            self.commanderDeaths = Math.min(9, self.commanderDeaths + 1);
        },
        decreaseCommanderDeaths() {
            if (self.commanderDeaths > 0) {
                self.commanderDeaths -= 1;
            }
        },
    }));

const AppStore = types
    .model('AppStore', {
        numberOfPlayers: types.optional(types.number, 2),
        startingHealth: types.optional(types.number, 40),
        players: types.optional(types.array(Player), []),
        showPlayerNames: types.optional(types.boolean, false),
    })
    .actions(self => {
        function setupPlayers() {
            availableColors = [...baseColors];
            self.players.clear();
            for (let i = 0; i < self.numberOfPlayers; i++) {
                self.players.push({
                    id: String(i + 1),
                    name: `Player ${i + 1}`,
                    health: self.startingHealth,
                    backgroundColor: getRandomColor(),
                    commanderDamage: [],
                });
            }
            updateCommanderDamageForPlayers();
        }
        function updateCommanderDamageForPlayers() {
            self.players.forEach(player => {
                player.commanderDamage.clear();
                self.players.forEach(opponent => {
                    player.commanderDamage.push({
                        opponentId: opponent.id,
                        damage: 0,
                        backgroundColor: opponent.backgroundColor,
                        isMe: opponent.id === player.id,
                    });
                });
            });
        }
        function afterCreate() {
            setupPlayers();
        }
        function setNumberOfPlayers(value: number) {
            self.numberOfPlayers = value;
            setupPlayers();
        }
        function setStartingHealth(value: number) {
            self.startingHealth = value;
            setupPlayers();
        }
        function increaseHealth(playerId: string, amount: number = 1) {
            const player = self.players.find(p => p.id === playerId);
            if (player) {
                player.increase(amount);
            }
        }
        function decreaseHealth(playerId: string, amount: number = 1) {
            const player = self.players.find(p => p.id === playerId);
            if (player) {
                player.decrease(amount);
            }
        }
        function togglePlayerNames() {
            self.showPlayerNames = !self.showPlayerNames;
        }
        return {
            afterCreate,
            setNumberOfPlayers,
            setStartingHealth,
            setupPlayers,
            increaseHealth,
            decreaseHealth,
            togglePlayerNames,
        };
    });

export const appStoreDefault = {
    numberOfPlayers: 4,
    startingHealth: 40,
    players: [],
};

export default AppStore;
